import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { DsConfirmDialogComponent } from '../components/ds-confirm-dialog/ds-confirm-dialog.component';
import { BydCanDeactivateInterface } from '../interfaces/byd-can-deactivate.interface';

@Injectable({
  providedIn: 'root',
})
export class CanDeactivateGuard implements CanDeactivate<BydCanDeactivateInterface> {
  /**
   * CanDeactivateGuard.
   *
   * @param dialog MatDialog
   * @param translate TranslateService
   */
  constructor(private dialog: MatDialog, private translate: TranslateService) {}

  /**
   * Returns whether the component can be left (built-in angular feature).
   * See: https://angular.io/api/router/CanDeactivate.
   *
   * @param component Current component
   * @param currentRoute Current route
   * @param currentState Current router state
   * @param nextState Next router state
   * @returns boolean
   */
  public canDeactivate(
    component: BydCanDeactivateInterface,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    const currentUrl: string = currentState.url;
    const nextUrl: string = nextState.url;
    return component?.canLeave() || currentUrl.split('?')[0] === nextUrl.split('?')[0]
      ? true
      : this.openConfirmationModal();
  }

  /**
   * Open confirmation Modal.
   *
   * @returns Observable<boolean>
   */
  private openConfirmationModal(): Observable<boolean> {
    return Observable.create((observer) => {
      this.dialog
        .open(DsConfirmDialogComponent, {
          data: {
            title: this.translate.instant('unsavedChanges.title'),
            message: this.translate.instant('unsavedChanges.leaveMessage'),
            cancelButton: this.translate.instant('dialog.cancel'),
            acceptButton: this.translate.instant('dialog.validate'),
          },
        })
        .afterClosed()
        .subscribe((result) => {
          observer.next(result);
          observer.complete();
        });
    });
  }
}
